import { CommonModule } from '@angular/common';
import { Component, EnvironmentInjector, Inject } from '@angular/core';
import { ResolveEnd, Router, RouterModule } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { extractValidRoutes } from 'src/app/core/services/guard-services/custom-redirect-guard.service';
import { LanguageService } from 'src/app/core/services/language.service';
import { FooterService } from 'src/app/services/footer.service';
import { LanguageSelectorService } from 'src/app/services/language-selecetro.service';
import { ClickOutsideDirective } from 'src/app/shared/directives/click-outside.directive';
import { LanguageRouterLinkDirective } from 'src/app/shared/directives/language-router-link.directive';
import { localizeRoute } from 'src/app/shared/utilities/localizeRoutes';

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    TranslocoDirective,
    ClickOutsideDirective,
    LanguageRouterLinkDirective,
  ],
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
})
export class FooterComponent {
  showDropdown = false;
  openDropdowns: { [key: string]: boolean } = {};
  showFooter: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  showAltFooter: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isOpen = false;
  selectedLanguage: string | undefined =
    this.translocoService.getActiveLang() === 'en' ? 'English' : 'Greek';
  toggleTranslation: Observable<boolean> = of(false);

  showLanguagePicker = this.languageSelectorService.getShowLanguagePicker();
  languagePickerHasTranslations =
    this.languageSelectorService.getLanguagePickerHasTranslations();

  localRoute = localizeRoute;
  activeLang = 'el';

  constructor(
    private router: Router,
    private translocoService: TranslocoService,
    private footerService: FooterService,
    private languageService: LanguageService,
    private languageSelectorService: LanguageSelectorService,
    @Inject(EnvironmentInjector) public envInjector: EnvironmentInjector
  ) { }

  ngOnInit(): void {
    this.toggleTranslation = this.footerService.toggleTranslation$;
    this.router.events.subscribe((routerData) => {
      if (routerData instanceof ResolveEnd) {
        const url = routerData.urlAfterRedirects || routerData.url;
        // Define footer visibility conditions
        const noFooterRoutes = [
          'on-boarding',
          'property-upload-wizard',
          'not-found',
          'error',
          'select-property',
        ];
        const altFooterRoutes = ['welcome', 'feedback', 'checkout'];

        // Update showFooter based on whether the URL matches any noFooterRoutes
        this.showFooter.next(
          !noFooterRoutes.some((route) => url.includes(route))
        );

        // Update showAltFooter based on whether the URL matches any altFooterRoutes
        this.showAltFooter.next(
          altFooterRoutes.some((route) => url.includes(route))
        );

        this.translocoService.langChanges$.subscribe(lang => {
          this.activeLang = lang;
        })
      }
    });
  }

  scrollToTop(): void {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }

  handleShowDropdown(id: string): void {
    this.showDropdown = !this.showDropdown;
    this.openDropdowns[id] = !this.openDropdowns[id];
  }

  setSelectedValue(event: Event) {
    const language = (event.target as HTMLButtonElement).value;
    this.openDropdowns['languages'] = false;
  }

  toggleDropdown() {
    this.isOpen = !this.isOpen;
  }

  selectLanguage(language: string) {
    let langToSet =
      language === 'English' ? 'en' : language === 'Greek' ? 'el' : 'translate';

    this.translocoService.setActiveLang(langToSet);
    this.languageService.setCurrentLang(langToSet);

    this.selectedLanguage = language;
    this.isOpen = false;

    const urlTree = this.router.parseUrl(this.router.url);

    const validRoutes = extractValidRoutes();
    if (!validRoutes.includes(this.getPathAfterLang(this.router.url))) {
      this.router.navigateByUrl(this.router.url);
    } else {
      const routeSegment = this.getPathAfterLang(this.router.url);

      const localizedRoute = localizeRoute(
        `/${routeSegment}`,
        this.envInjector
      ); // Call localizeRoute to trigger the URL update

      // Strip query parameters from localizedRoute
      const localizedRouteUrl = new URL(localizedRoute, window.location.origin); // Use a base URL for parsing
      localizedRouteUrl.search = ''; // Remove query string
      const cleanedLocalizedRoute = localizedRouteUrl.pathname;

      const newUrlTree = this.router.createUrlTree([cleanedLocalizedRoute], {
        // Use the localized route directly
        queryParams: urlTree.queryParams,
        fragment: urlTree.fragment as string | undefined,
      });

      const newUrl = this.router.serializeUrl(newUrlTree);

      this.router.navigateByUrl(newUrl);
    }
  }

  getPathAfterLang(url: string) {
    const parts = url.split('/');
    const langIndex = 1; // Assuming the language code is always the second part
    const pathAfterLang = parts.slice(langIndex + 1).join('/');
    return pathAfterLang;
  }
}
